import React from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import {SOURCES_DIALOG_STACK_PROPS} from './sourcesDialogStackProps';
import ClassificationFieldsGroup from '../../../../../components/classification/ClassificationFieldsGroup';
import useClassificationFields from '../../../../../components/classification/useClassificationFields';
import useSourcesUploadQueueStore from './useSourcesUploadQueueStore';
import {looksLikeHtml} from '../../../../../utils/sourceImportHelpers';

/**
 * Dedicated "Add source" dialog for the Copy & Paste entry point: pasted text/HTML →
 * POST /api/data-items/from-content, handed to `onSubmitWebImports` (goes through the
 * web-import queue).
 *
 * Classification Level / Distribution / Compartment are optional.
 */
const SourcesPasteImportDialog = ({open, onClose, onSubmitWebImports}) => {
    const defaultClassificationLevel = useSourcesUploadQueueStore((state) => state.classificationLevel);
    const [pasteText, setPasteText] = React.useState('');
    const [pasteName, setPasteName] = React.useState('');
    const {reset: resetClassification, toPayload: classificationPayload, fieldsProps: classificationFieldsProps} =
        useClassificationFields(defaultClassificationLevel);
    const [error, setError] = React.useState(null);

    const resetState = React.useCallback(() => {
        setPasteText('');
        setPasteName('');
        resetClassification();
        setError(null);
    }, [resetClassification]);

    const handleClose = React.useCallback(() => {
        resetState();
        onClose?.();
    }, [onClose, resetState]);

    const handleSubmit = React.useCallback(() => {
        setError(null);
        const text = String(pasteText || '').trim();
        if (!text) {
            setError('Paste some content to import.');
            return;
        }
        // Treat content that looks like HTML as HTML; otherwise as plain text.
        const isHtml = looksLikeHtml(text);
        const entry = {
            sourceKind: 'paste',
            name: String(pasteName || '').trim() || undefined,
            htmlContent: isHtml ? text : undefined,
            plainText: isHtml ? undefined : text,
            ...classificationPayload(),
        };
        onSubmitWebImports?.([entry]);
        handleClose();
    }, [classificationPayload, handleClose, onSubmitWebImports, pasteName, pasteText]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            {...SOURCES_DIALOG_STACK_PROPS}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <NotesRoundedIcon fontSize="small" color="action"/>
                    <span>Add source: Raw text</span>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label="Title (optional)"
                        placeholder="Untitled pasted content"
                        value={pasteName}
                        onChange={(event) => setPasteName(event.target.value)}
                        fullWidth
                        size="small"
                    />
                    <ClassificationFieldsGroup
                        idPrefix="paste-classification"
                        {...classificationFieldsProps}
                    />
                    <TextField
                        label="Content"
                        placeholder="Paste text or HTML here…"
                        value={pasteText}
                        onChange={(event) => setPasteText(event.target.value)}
                        fullWidth
                        multiline
                        minRows={6}
                        helperText="Paste plain text or HTML. Pasted content is converted to a document and added to your files."
                    />
                </Stack>

                {error && (
                    <Alert severity="error" sx={{mt: 2}}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!pasteText.trim()}>
                    Add source
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(SourcesPasteImportDialog);
